import { createStore } from 'zustand'
import { StatsInitialState, StatsStore } from './types'
import {
  getYearStatus,
  isOtherPlusUser,
  isSelfPlusUser,
  isStatsSelfView,
} from '../../utils'
import {
  getStatsYearList,
  getUserStats,
  updateStatsPrivacy,
} from '../../requests'

export const createStatsStore = ({
  userId,
  isSelfView,
  ...initialState
}: StatsInitialState) => {
  return createStore<StatsStore>()((set, get) => ({
    ...initialState,
    stats: null,
    year: new Date().getFullYear(),
    streakMonth: new Date().getMonth() + 1,
    statsUserId: userId || '',
    privacy: 'me',
    yearList: [],
    status: 'loading',
    isSharing: false,
    shareModalOptions: null,
    debug: false,
    isSelfView: isSelfView || isStatsSelfView,
    error: null,
    yearStatus: 'current',
    authUser: null,
    isPlusUser: false,
    setStatus: (status) => set({ status }),
    setDebug: (debug) => set({ debug }),
    setStatsUserId: (statsUserId) => set({ statsUserId }),
    setPrivacy: (privacy) => set({ privacy }),
    setStreakMonth: (streakMonth) => set({ streakMonth }),
    setYear: (year) => set({ year }),
    openShareModal: (options) =>
      set({ isSharing: true, shareModalOptions: options }),
    closeShareModal: () => {
      set({ isSharing: false })

      setTimeout(() => {
        set({ shareModalOptions: null })
      }, 400)
    },
    getYearList: async () => {
      const { statsUserId } = get()

      try {
        const { data } = await getStatsYearList(statsUserId)

        const minYear = 2024
        const yearList =
          data?.years.map((y) => y).filter((s) => s >= minYear) || []

        const currentYear = new Date().getFullYear()
        const isDecember = new Date().getMonth() === 11
        if (isDecember && !yearList.includes(currentYear + 1)) {
          yearList.unshift(currentYear + 1)
        }

        set({ yearList })
      } catch (e) {
        console.error('Error getting year list', e)
      }
    },
    updatePrivacy: async (privacy) => {
      const { statsUserId } = get()

      if (!statsUserId) return

      try {
        await updateStatsPrivacy({
          userId: statsUserId,
          isPrivate: privacy === 'me',
        })
        set({ privacy })
      } catch (e) {
        console.error('Error updating stats privacy', e)
      }
    },
    setStats: async (stats) => {
      set({
        stats,
        status: 'idle',
        privacy: stats.privacy,
      })
    },
    getStats: async ({ status = 'loading' } = {}) => {
      const { statsUserId, year, getYearList, isSelfView, authUser } = get()

      if (!statsUserId) return null

      set({ status })

      try {
        const [res] = await Promise.all([
          getUserStats({ userId: statsUserId, year }),
          status !== 'loadingYear' ? getYearList() : Promise.resolve(),
        ])

        const statsData = res.data ?? []

        if (!statsData.length) {
          throw new Error('No Stats found')
        }

        const stats = statsData[0]
        const yearStatus = getYearStatus(stats)

        const isPlusUser = isSelfView
          ? isSelfPlusUser(authUser)
          : isOtherPlusUser(stats.user)

        set({
          status: 'idle',
          stats,
          privacy: stats.privacy,
          yearStatus,
          streakMonth: yearStatus === 'past' ? 12 : new Date().getMonth() + 1,
          isPlusUser,
        })

        return stats
      } catch (e) {
        const err = e as Error
        console.error(`Stats error: ${err.message || 'Unknown error'}`)
        set({ status: 'error' })
        return null
      }
    },
    setAuthUser: (user) => set({ authUser: user }),
  }))
}
